import {
	AvTimer as AvTimerIcon,
	DirectionsCar as DirectionsCarIcon,
	Speed as SpeedIcon,
	InfoOutlined as InfoOutlinedIcon
} from "@mui/icons-material";
import { Avatar, Box, Grid2, Skeleton, styled, Tooltip, tooltipClasses, TooltipProps, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import ReactECharts from "echarts-for-react";
import Leaflet from "leaflet";
import "leaflet/dist/leaflet.css";
import { orderBy } from "lodash";
import moment from "moment";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";

import { AGDataGrid } from "../../components/AGDataGrid";
import { useAppSelector } from "../../redux";
import { useGetInterceptorsListQuery } from "../../redux/reducers/configuration.reducer";
import {
	useGetDashboardLineChartQuery,
	useGetDashboardMetricsQuery,
	useGetViolationRatePieChartDataQuery,
	useGetViolationTrendsDataQuery
} from "../../redux/reducers/dashboard.reducer";
import {
	IGetDashboardLineChartsRequestData,
	IGetDashboardMetricsRequestData,
	IInterceptorDetails,
	INTERCEPTOR_STATUS_E
} from "../../types";
import { convertSnakeToTitleCase, formatNumberMetric } from "../../utils/commonUtils";
import { NON_HSRP_VIOLATION_NAMES, TIME_FILTER_FORMAT } from "../../utils/constants";

const statusColor: Record<INTERCEPTOR_STATUS_E, string> = {
	[INTERCEPTOR_STATUS_E.ONLINE]: "#43A047",
	[INTERCEPTOR_STATUS_E.OFFLINE]: "#E53935",
	[INTERCEPTOR_STATUS_E.UNKNOWN]: "#969696"
};

interface InterceptorMapProps {
	interceptors: IInterceptorDetails[];
	selectedId: number;
	onSelect: (id: number) => void;
}

enum VOLUME_TAB_E {
	VEHICLE,
	VIOLATION
}

const DashboardTooltip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: theme.palette.primary.main,
		textAlign: "center",
		padding: "1rem",
		fontWeight: 500,
		maxWidth: "300px"
	},
	[`& .${tooltipClasses.arrow}`]: {
		color: theme.palette.primary.main,
		width: "28px",
		height: "20px"
	},
	[`& .${tooltipClasses.tooltipPlacementTop} .${tooltipClasses.arrow}`]: {
		marginBottom: "-20px"
	}
}));

const InterceptorMap: FC<InterceptorMapProps> = ({ interceptors, selectedId, onSelect }) => {
	const markerRefs = useRef<(Leaflet.Marker | null)[]>(new Array(interceptors.length).fill(null));

	useEffect(() => {
		const selectedMarkerIndex = interceptors.findIndex((item) => item.id === selectedId);

		if (selectedMarkerIndex >= 0) {
			markerRefs.current[selectedMarkerIndex]?.fire("click");
		}
	}, [interceptors, selectedId]);

	return (
		<MapContainer
			center={[19.5, 75.5]}
			zoom={7}
			style={{ width: "100%", height: 350, borderRadius: 12, zIndex: 1 }}
			dragging={true}
			attributionControl={false}
			minZoom={3}
		>
			<TileLayer url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}" attribution="© Google" />
			{interceptors.map((interceptorItem, index) => (
				<Marker
					key={interceptorItem.id}
					position={[
						interceptorItem.location_cord_latitude ? Number(interceptorItem.location_cord_latitude) : 0,
						interceptorItem.location_cord_longitude ? Number(interceptorItem.location_cord_longitude) : 0
					]}
					icon={Leaflet.divIcon({
						className: "custom-marker",
						html: `<div style='background:${
							statusColor[interceptorItem.is_active]
						}22;border-radius:50%;width:38px;height:38px;display:flex;align-items:center;justify-content:center;'><div style='background:${
							statusColor[interceptorItem.is_active]
						};color:white;width:22px;height:22px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:600;border:2px solid white;'>${
							index + 1
						}</div></div>`
					})}
					eventHandlers={{ click: () => onSelect(interceptorItem.id) }}
					ref={(element) => (markerRefs.current[index] = element)}
				>
					<Popup>
						<b>{interceptorItem.numberplate}</b>
						<br />
						{interceptorItem.location_name}
					</Popup>
				</Marker>
			))}
		</MapContainer>
	);
};

const DashboardTab: FC = () => {
	const filtersState = useAppSelector((state) => state.filters);
	const printRef = useRef<HTMLDivElement>(null);

	const [volumeTab, setVolumeTab] = useState<VOLUME_TAB_E>(VOLUME_TAB_E.VEHICLE);
	const [selectedInterceptor, setSelectedInterceptor] = useState<number>(0);

	const filters = useMemo<IGetDashboardMetricsRequestData>(
		() => ({
			start_date: moment(filtersState.timeFilter.start_time, TIME_FILTER_FORMAT).format("DD-MM-YYYY_HH:mm:ss"),
			end_date: moment(filtersState.timeFilter.end_time, TIME_FILTER_FORMAT).format("DD-MM-YYYY_HH:mm:ss")
		}),
		[filtersState.timeFilter]
	);

	const volumeTrendGraphFilters = useMemo<IGetDashboardLineChartsRequestData>(
		() => ({
			from: filtersState.timeFilter.start_time,
			to: filtersState.timeFilter.end_time,
			chart: volumeTab === VOLUME_TAB_E.VEHICLE ? "vehicle_type" : "violation"
		}),
		[filtersState.timeFilter, volumeTab]
	);

	// APIS
	// GET DASHBOARD METRICS
	const {
		data: dashboardMetrics,
		isLoading: dashboardMetricsLoading,
		isFetching: dashboardMetricsFetching
	} = useGetDashboardMetricsQuery(filters);

	// GET DASHBOARD LINE CHART
	const {
		data: vehicleColumeChartData,
		isLoading: vehicleVolumeLoading,
		isFetching: vehicleVolumeFetching
	} = useGetDashboardLineChartQuery(volumeTrendGraphFilters);

	// GET VIOLATION RATE PIE CHART DATA
	const {
		data: violationRatePieChartData,
		isLoading: violationRateLoading,
		isFetching: violationRateFetching
	} = useGetViolationRatePieChartDataQuery(filters);

	// GET INTERCEPTORS LIST
	const {
		data: interceptorsData,
		isLoading: interceptorsLoading,
		isFetching: interceptorsFetching
	} = useGetInterceptorsListQuery({ page: 1 });

	// GET VIOLATION TRENDS DATA
	const { data: violationTrendsData } = useGetViolationTrendsDataQuery(filters);

	const summaryStats = useMemo(
		() => [
			{
				icon: <DirectionsCarIcon fontSize="medium" />,
				label: "Total Violations",
				value: formatNumberMetric(dashboardMetrics?.violation_count ?? 0),
				gradient: "linear-gradient(135deg, #E0F0FF 0%, #F3EFFF 100%)",
				iconBg: "#E0F0FF",
				iconColor: "#388DFF"
			},
			{
				icon: <AvTimerIcon fontSize="medium" />,
				label: "System Uptime",
				value: dashboardMetrics ? `${formatNumberMetric(dashboardMetrics.system_uptime_percent)}%` : "--",
				valueColor: "success.main",
				gradient: "linear-gradient(135deg, #E8F5E9 0%, #D0F2DF 100%)",
				iconBg: "#E8F5E9",
				iconColor: "#43A047"
			},
			{
				icon: <SpeedIcon fontSize="medium" />,
				label: "System Downtime",
				value: dashboardMetrics ? `${formatNumberMetric(100 - dashboardMetrics.system_uptime_percent)}%` : "--",
				valueColor: "error.main",
				gradient: "linear-gradient(135deg, #FEE8E8 0%, #F8D7E3 100%)",
				iconBg: "#FEE8E8",
				iconColor: "#6A040F"
			}
		],
		[dashboardMetrics]
	);

	const vehicleVolumeTrendOptions = useMemo(
		() => ({
			color: ["#FF1717"],
			tooltip: {
				trigger: "axis",
				confine: true,
				formatter: (params: any) => {
					if (!vehicleColumeChartData) return "";

					const details = vehicleColumeChartData[params[0].name];

					return `<div>
						<p style="font-size: 12px; color: #00000080; text-align: center">${params[0].name}</p>

						<div style="display: flex; align-items: center; justify-content: space-between; gap: 0.5rem">
							<p style="font-weight: 600; margin-block: 0.25rem">
								${volumeTab === VOLUME_TAB_E.VEHICLE ? "Total Vehicles" : "Total Violations"}
							</p>
							<p style="font-weight: 600; margin-block: 0.25rem">${formatNumberMetric(params[0].value)}</p>
						</div>

						${orderBy(Object.entries(details), ([_key, count]) => count * -1)
							.filter(([_key, count]) => count > 0)
							.map(([key, count]) =>
								key === "total_count" || key === "timestamp_data"
									? null
									: `<div style="display: flex; align-items: center; justify-content: space-between; gap: 1rem">
											<p style="margin-block: 0.25rem; text-transform: capitalize">${
												volumeTab === VOLUME_TAB_E.VEHICLE ? key.replace(/_/g, " ") : String(key).replace(/_/g, " ")
											}</p>
											<p style="margin-block: 0.25rem">${formatNumberMetric(count)}</p>
										</div>`
							)
							.join("")}
					</div>`;
				}
			},
			grid: { left: 0, right: 25, bottom: 0, containLabel: true },
			xAxis: {
				type: "category",
				data: vehicleColumeChartData ? Object.keys(vehicleColumeChartData) : [],
				boundaryGap: false,
				axisLine: { show: false }
			},
			yAxis: {
				type: "value",
				axisLine: { show: false },
				axisLabel: { formatter: (value: number) => formatNumberMetric(value) },
				splitLine: { lineStyle: { color: "#f0f0f0" } }
			},
			series: [
				{
					data: vehicleColumeChartData ? Object.values(vehicleColumeChartData).map((item) => item.total_count) : [],
					type: "line",
					smooth: false,
					symbol: "circle",
					symbolSize: 8,
					areaStyle: { color: "rgba(255,23,23,0.08)" }
				}
			]
		}),
		[vehicleColumeChartData, volumeTab]
	);

	const violationRateOptions = useMemo(
		() => ({
			color: ["#43A047", "#E53935", "#FFC000"],
			tooltip: {
				trigger: "item",
				formatter: (params: any) => `${params.name}: ${formatNumberMetric(params.value)} (${formatNumberMetric(params.percent)}%)`
			},
			legend: {
				show: false
			},
			series: [
				{
					type: "pie",
					radius: ["60%", "90%"],
					avoidLabelOverlap: false,
					label: { show: false },
					data: [
						{
							value: violationRatePieChartData?.counts.approved.count ?? 0,
							name: violationRatePieChartData?.counts.approved.label ?? "Approved"
						},
						{
							value: violationRatePieChartData?.counts.reject.count ?? 0,
							name: violationRatePieChartData?.counts.reject.label ?? "Rejected"
						},
						{
							value: violationRatePieChartData?.counts.pending.count ?? 0,
							name: violationRatePieChartData?.counts.pending.label ?? "Pending"
						}
					]
				}
			]
		}),
		[violationRatePieChartData]
	);

	const violationTrendRows = useMemo(
		() =>
			violationTrendsData?.violation_metrics.map((item, index) => ({
				id: index,
				type: NON_HSRP_VIOLATION_NAMES.includes(item.violation_name)
					? "Non HSRP"
					: convertSnakeToTitleCase(item.violation_name),
				count: item.count,
				change:
					item.percent_change === "N/A" ? item.percent_change : Number((item.percent_change * 100).toFixed(0)) / 100
			})),
		[violationTrendsData?.violation_metrics]
	);

	function renderPercentChangeHeader() {
		return (
			<Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
				<Typography variant="subtitle2" overflow="hidden" textOverflow="ellipsis">
					Change In %
				</Typography>

				{violationTrendsData ? (
					<DashboardTooltip
						arrow
						placement="top"
						title={
							<span>
								In comparison with data from
								<br />
								{`${moment(violationTrendsData.comparison_info.prev_start_date).format(
									"DD/MM/YYYY hh:mm A"
								)} - ${moment(violationTrendsData.comparison_info.prev_end_date).format("DD/MM/YYYY hh:mm A")}`}
							</span>
						}
					>
						<InfoOutlinedIcon fontSize="inherit" />
					</DashboardTooltip>
				) : null}
			</Box>
		);
	}

	const violationTrendColumns: GridColDef[] = [
		{
			field: "type",
			headerName: "Violation Type",
			flex: 1,
			minWidth: 120,
			headerAlign: "left",
			align: "left",
			sortable: false,
			filterable: false
		},
		{
			field: "count",
			headerName: "Total Count",
			flex: 0.7,
			minWidth: 80,
			headerAlign: "center",
			align: "center",
			sortable: false,
			filterable: false,
			valueFormatter: (value) => formatNumberMetric(value)
		},
		{
			field: "change",
			headerName: "Change In %",
			flex: 0.7,
			minWidth: 100,
			headerAlign: "center",
			align: "center",
			sortable: false,
			filterable: false,
			renderHeader: renderPercentChangeHeader,
			renderCell: (params) => {
				if (params.value === "N/A") {
					return <Box className="violation-trend-value">N/A</Box>;
				}

				const value = params.value as number;
				return (
					<Box className={`violation-trend-value ${value < 0 ? "negative" : "positive"}`}>
						{`${value < 0 ? "▼" : "▲"}${formatNumberMetric(Math.abs(value))}%`}
					</Box>
				);
			}
		}
	];

	useEffect(() => {
		if (interceptorsData && interceptorsData.results.length > 0) {
			setSelectedInterceptor(interceptorsData.results[0].id);
		}
	}, [interceptorsData]);

	return (
		<Box className="page-content-wrapper">
			<Box sx={{ flex: 1, height: 0, display: "flex", flexDirection: "column", overflowY: "auto" }}>
				<Grid2 container spacing={2}>
					{summaryStats.map((stat) => (
						<Grid2 key={stat.label} size={{ xs: 12, md: 4 }}>
							{dashboardMetricsLoading || dashboardMetricsFetching ? (
								<Box className="dashboard-summary-stats-card" sx={{ background: stat.gradient }}>
									<Skeleton variant="circular" width={48} height={48} />
									<Box sx={{ flex: 1 }}>
										<Skeleton variant="text" width="60%" height={20} />
										<Skeleton variant="text" width="40%" height={32} />
									</Box>
								</Box>
							) : (
								<Box className="dashboard-summary-stats-card" sx={{ background: stat.gradient }}>
									<Avatar
										classes={{ root: "dashboard-summary-stat-avatar-root" }}
										sx={{ bgcolor: stat.iconBg, color: stat.iconColor }}
									>
										{stat.icon}
									</Avatar>
									<Box>
										<Typography
											variant="body2"
											color="text.secondary"
											sx={{ fontWeight: 500, letterSpacing: 0.2, mb: 0.5 }}
										>
											{stat.label}
										</Typography>

										<Typography
											variant="h4"
											fontWeight={700}
											color={stat.valueColor ? stat.valueColor : "text.primary"}
											sx={{ letterSpacing: -1, lineHeight: 1.1 }}
										>
											{stat.value}
										</Typography>
									</Box>
								</Box>
							)}
						</Grid2>
					))}

					<Grid2 size={{ xs: 12, md: 8 }}>
						<Box className="dashboard-graph-card">
							<Box className="dashboard-volume-trend-graph-header">
								<Box>
									<Typography variant="h6" fontWeight={700} sx={{ fontSize: 20, letterSpacing: -0.5, mb: 0.2 }}>
										Volume Trend
									</Typography>

									<Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, fontSize: 14 }}>
										Number of vehicles detected over time
									</Typography>
								</Box>
								<Box role="tablist" aria-label="Volume Trend Tabs" className="volume-trend-tabs-selection">
									{[VOLUME_TAB_E.VEHICLE, VOLUME_TAB_E.VIOLATION].map((tab) => (
										<Box
											key={tab}
											role="tab"
											aria-selected={volumeTab === tab}
											tabIndex={0}
											onClick={() => setVolumeTab(tab)}
											onKeyDown={(e) => {
												if (e.key === "Enter" || e.key === " ") setVolumeTab(tab);
											}}
											className={`volume-trend-tab ${volumeTab === tab ? "active" : ""}`}
										>
											{tab === VOLUME_TAB_E.VEHICLE ? "Vehicle" : "Violation"}
										</Box>
									))}
								</Box>
							</Box>

							{vehicleVolumeLoading || vehicleVolumeFetching ? (
								<Box sx={{ minHeight: "200px", width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
									<Skeleton variant="rectangular" height={150} />
									<Skeleton variant="text" width="30%" />
									<Skeleton variant="text" width="50%" />
								</Box>
							) : !vehicleVolumeTrendOptions.series[0].data.some((item) => item > 0) ? (
								<Box className="volume-trend-no-data-wrapper">
									<Box className="line-chart-no-data-overlay">
										<Typography>No data</Typography>
									</Box>
								</Box>
							) : (
								<ReactECharts option={vehicleVolumeTrendOptions} style={{ minHeight: "200px", width: "100%" }} />
							)}
						</Box>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 4 }}>
						<Box className="dashboard-graph-card">
							<Typography variant="h6" fontWeight={700} mb={1} sx={{ fontSize: 20, letterSpacing: -0.5 }}>
								Violation Rate
							</Typography>

							<Typography variant="body2" color="text.secondary" mb={2} sx={{ fontWeight: 400, fontSize: 14 }}>
								Percentage of violation approved vs rejected rate
							</Typography>

							{violationRateLoading || violationRateFetching ? (
								<Box sx={{ position: "relative", flex: 1 }}>
									<Skeleton variant="circular" width={200} height={200} sx={{ mx: "auto" }} />
									<Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
										<Skeleton variant="text" width={60} />
										<Skeleton variant="text" width={60} />
										<Skeleton variant="text" width={60} />
									</Box>
								</Box>
							) : (
								<>
									<Box sx={{ position: "relative", flex: 1 }}>
										<ReactECharts option={violationRateOptions} style={{ minHeight: "200px", width: "100%" }} />

										<Box
											sx={{
												position: "absolute",
												top: "50%",
												left: "50%",
												transform: "translate(-50%, -50%)",
												maxWidth: "60%"
											}}
										>
											<Typography fontSize="32px" textAlign="center" color="#00000099" fontWeight={500}>
												{formatNumberMetric(violationRatePieChartData?.total_count ?? 0)}
											</Typography>

											<Typography textAlign="center" fontWeight={500} color="#00000099">
												Total Vehicles
											</Typography>
										</Box>
									</Box>

									<Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 1 }}>
										<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
											<Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#43A047" }} />
											<Typography variant="body2">Approved</Typography>
										</Box>

										<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
											<Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#E53935" }} />
											<Typography variant="body2">Rejected</Typography>
										</Box>

										<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
											<Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "#FFC000" }} />
											<Typography variant="body2">Pending</Typography>
										</Box>
									</Box>
								</>
							)}
						</Box>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 5 }}>
						<Box className="dashboard-graph-card" sx={{ display: "flex", flexDirection: "column" }}>
							<Typography
								variant="h6"
								fontWeight={700}
								sx={{ fontSize: 20, letterSpacing: -0.5, marginBottom: "1rem" }}
							>
								Violation Trend
							</Typography>

							<Box sx={{ flex: 1, minHeight: 200 }}>
								{/* Static data, no loading needed */}
								<AGDataGrid hideFooter columns={violationTrendColumns} rows={violationTrendRows} />
							</Box>
						</Box>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 7 }}>
						<Box className="dashboard-graph-card">
							<Typography variant="h6" fontWeight={700} mb={2}>
								Interceptor Locations
							</Typography>

							<Box sx={{ flex: 1, borderRadius: 2, overflow: "hidden", position: "relative" }}>
								{interceptorsLoading || interceptorsFetching ? (
									<Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
										<Skeleton variant="rectangular" height={350} />
										<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
											<Skeleton variant="text" width={100} />
											<Skeleton variant="text" width={150} />
										</Box>
									</Box>
								) : (
									<InterceptorMap
										interceptors={interceptorsData?.results ?? []}
										selectedId={selectedInterceptor}
										onSelect={setSelectedInterceptor}
									/>
								)}
							</Box>
						</Box>
					</Grid2>
				</Grid2>

				{/* Print-only div for PDF generation */}
				<Box
					ref={printRef}
					sx={{
						display: "none",
						position: "absolute",
						top: "-9999px",
						left: "-9999px",
						width: "210mm", // A4 width
						backgroundColor: "#ffffff",
						padding: "20px",
						fontFamily: "Arial, sans-serif",
						"& *": {
							color: "#000000 !important",
							backgroundColor: "transparent !important"
						}
					}}
				>
					{/* Header */}
					<Box sx={{ textAlign: "center", mb: 3, borderBottom: "2px solid #1976d2", pb: 2 }}>
						<Typography variant="h4" sx={{ color: "#1976d2", fontWeight: "bold", mb: 1 }}>
							EdgeTrack Dashboard Report
						</Typography>
						<Typography variant="body1" sx={{ color: "#666666" }}>
							Generated on: {moment().format("DD-MM-YYYY HH:mm:ss")}
						</Typography>
						<Typography variant="body1" sx={{ color: "#666666" }}>
							Time Range: {moment(filtersState.timeFilter.start_time, TIME_FILTER_FORMAT).format("DD-MM-YYYY HH:mm")} to{" "}
							{moment(filtersState.timeFilter.end_time, TIME_FILTER_FORMAT).format("DD-MM-YYYY HH:mm")}
						</Typography>
					</Box>

					{/* Summary Statistics */}
					<Box sx={{ mb: 3 }}>
						<Typography variant="h5" sx={{ fontWeight: "bold", mb: 2, color: "#000000" }}>
							Summary Statistics
						</Typography>
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
							{summaryStats.map((stat) => (
								<Box
									key={stat.label}
									sx={{
										flex: "1 1 200px",
										p: 2,
										border: "1px solid #ddd",
										borderRadius: 1,
										backgroundColor: "#f9f9f9"
									}}
								>
									<Typography variant="body2" sx={{ color: "#666666", mb: 0.5 }}>
										{stat.label}
									</Typography>
									<Typography variant="h6" sx={{ fontWeight: "bold", color: "#000000" }}>
										{stat.value}
									</Typography>
								</Box>
							))}
						</Box>
					</Box>

					{/* Volume Trend Chart */}
					<Box sx={{ mb: 3 }}>
						<Typography variant="h5" sx={{ fontWeight: "bold", mb: 2, color: "#000000" }}>
							Volume Trend - {volumeTab === VOLUME_TAB_E.VEHICLE ? "Vehicle" : "Violation"}
						</Typography>
						<Box sx={{ height: "300px", width: "100%" }}>
							{vehicleVolumeLoading || vehicleVolumeFetching ? (
								<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
									<Typography>Loading chart...</Typography>
								</Box>
							) : !vehicleVolumeTrendOptions.series[0].data.some((item) => item > 0) ? (
								<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
									<Typography>No data available</Typography>
								</Box>
							) : (
								<ReactECharts option={vehicleVolumeTrendOptions} style={{ height: "100%", width: "100%" }} />
							)}
						</Box>
					</Box>

					{/* Violation Rate Chart */}
					<Box sx={{ mb: 3 }}>
						<Typography variant="h5" sx={{ fontWeight: "bold", mb: 2, color: "#000000" }}>
							Violation Rate Analysis
						</Typography>
						<Box sx={{ height: "300px", width: "100%" }}>
							{violationRateLoading || violationRateFetching ? (
								<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
									<Typography>Loading chart...</Typography>
								</Box>
							) : (
								<ReactECharts option={violationRateOptions} style={{ height: "100%", width: "100%" }} />
							)}
						</Box>
					</Box>

					{/* Violation Trends Table */}
					<Box sx={{ mb: 3 }}>
						<Typography variant="h5" sx={{ fontWeight: "bold", mb: 2, color: "#000000" }}>
							Violation Trends
						</Typography>
						<Box sx={{ border: "1px solid #ddd", borderRadius: 1 }}>
							{violationTrendRows?.map((row, index) => (
								<Box
									key={index}
									sx={{
										display: "flex",
										p: 2,
										borderBottom: index < (violationTrendRows?.length || 0) - 1 ? "1px solid #eee" : "none",
										backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff"
									}}
								>
									<Box sx={{ flex: 1 }}>
										<Typography variant="body1" sx={{ fontWeight: "bold", color: "#000000" }}>
											{row.type}
										</Typography>
									</Box>
									<Box sx={{ flex: 0.5, textAlign: "center" }}>
										<Typography variant="body1" sx={{ color: "#000000" }}>
											{formatNumberMetric(row.count)}
										</Typography>
									</Box>
									<Box sx={{ flex: 0.5, textAlign: "center" }}>
										<Typography
											variant="body1"
											sx={{
												color: row.change === "N/A" ? "#666666" : row.change < 0 ? "#E53935" : "#43A047",
												fontWeight: "bold"
											}}
										>
											{row.change === "N/A" ? "N/A" : `${row.change > 0 ? "+" : ""}${formatNumberMetric(row.change)}%`}
										</Typography>
									</Box>
								</Box>
							))}
						</Box>
					</Box>

					{/* Footer */}
					<Box sx={{ textAlign: "center", mt: 4, pt: 2, borderTop: "1px solid #ddd" }}>
						<Typography variant="body2" sx={{ color: "#999999" }}>
							Generated by EdgeTrack System
						</Typography>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default DashboardTab;
